<template>
  <DxPopup
    :visible="visible"
    :hide-on-outside-click="false"
    :show-close-button="false"
    :drag-enabled="false"
    :resize-enabled="false"
    :width="950"
    :height="585"
    @hidden="$emit('hidden')"
  >
    <template #title>
      <div class="wli-title-wrap">
        <div class="wli-title-main">{{ popupTitle }}</div>
        <div class="wli-title-sub">
          {{ productName }}
          <span v-if="measureUnitSymbol"> | {{ targetQuantity }} {{ measureUnitSymbol }}</span>
          <span> | {{ targetQuantityKg }} kg</span>
        </div>
      </div>
    </template>

    <template #content>
      <div class="wli-body">
        <div class="wli-toolbar" v-if="mode === 'reception'">
          <SmagriaButton :label="$t('lot_items.add')" customClass="secondary border" :width="'140px'" :action="addReceptionRow" />
        </div>

        <DxDataGrid
          ref="grid"
          class="smagria-grid"
          :data-source="rows"
          key-expr="row_key"
          :show-borders="true"
          :column-auto-width="true"
          :remote-operations="false"
          :hover-state-enabled="true"
          height="410"
          @cell-prepared="onCellPrepared"
          @row-updated="onRowUpdated"
        >
          <DxEditing
            mode="cell"
            :allow-updating="true"
            :allow-deleting="mode === 'reception'"
            :use-icons="true"
            :confirm-delete="false"
          />
          <DxScrolling mode="virtual" />
          <DxSorting mode="none" />

          <DxColumn data-field="lot" :caption="$t('lot_items.lot')" :allow-editing="mode === 'reception'" :min-width="180" />
          <DxColumn
            data-field="expiration_date"
            :caption="$t('common.expiration_date')"
            data-type="date"
            format="yyyy-MM-dd"
            :allow-editing="mode === 'reception'"
            :min-width="160"
          />
          <DxColumn
            data-field="available_quantity"
            :caption="$t('lot_items.available_qty')"
            data-type="number"
            :allow-editing="false"
            :visible="mode === 'sales'"
            alignment="right"
            :min-width="120"
          />
          <DxColumn data-field="quantity" :caption="$t('common.quantity')" data-type="number" alignment="right" :min-width="120" />
          <DxColumn
            data-field="quantity_kg"
            caption="kg"
            data-type="number"
            :allow-editing="false"
            alignment="right"
            :min-width="120"
          />
          <DxColumn
            data-field="measure_unit.name"
            :caption="$t('common.measure_unit')"
            :allow-editing="false"
            :allow-filtering="false"
            :allow-sorting="false"
            :min-width="100"
          />
        </DxDataGrid>

        <div class="wli-summary">
          <span>{{ $t('common.quantity') }}: {{ selectedTotal }} {{ measureUnitSymbol }}</span>
          <span>|</span>
          <span>kg: {{ selectedTotalKg }}</span>
        </div>

        <small v-if="error" class="text-danger wli-error">{{ error }}</small>

        <div class="wli-actions">
          <SmagriaButton :label="$t('common.cancel')" customClass="secondary border" :width="'150px'" :action="cancel" />
          <SmagriaButton :label="$t('lot_items.save')" customClass="primary" :width="'180px'" :action="confirm" />
        </div>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { DxDataGrid, DxColumn, DxEditing, DxScrolling, DxSorting } from "devextreme-vue/data-grid";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import { splitQuantityAcrossLotRows } from "~/helpers/stockHelper";

export default {
  name: "WeighingLotItemsPopup",
  components: {
    DxPopup,
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxScrolling,
    DxSorting,
    SmagriaButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "reception", // reception | sales
    },
    productName: {
      type: String,
      default: "",
    },
    measureUnitSymbol: {
      type: String,
      default: "",
    },
    targetQuantity: {
      type: Number,
      default: 0,
    },
    targetQuantityKg: {
      type: Number,
      default: 0,
    },
    conversionFactorKgPerUnit: {
      type: Number,
      default: 1,
    },
    initialRows: {
      type: Array,
      default: () => [],
    },
    epsilon: {
      type: Number,
      default: 0.0001,
    },
  },
  data() {
    return {
      rows: [],
      error: "",
    };
  },
  computed: {
    popupTitle() {
      return this.mode === "sales" ? this.$t("lot_items.available_title") : this.$t("lot_items.edit");
    },
    selectedTotal() {
      return this.round(this.rows.reduce((sum, row) => sum + (parseFloat(row.quantity) || 0), 0));
    },
    selectedTotalKg() {
      return this.round(this.rows.reduce((sum, row) => sum + (parseFloat(row.quantity_kg) || 0), 0));
    },
  },
  watch: {
    visible(newVal) {
      if (!newVal) {
        return;
      }
      this.error = "";
      this.rows = (this.initialRows || []).map((row, idx) => this.normalizeRow(row, idx));
      if (this.mode === "sales") {
        this.rows = this.rows.filter(
          (row) => Array.isArray(row.lot_rows) && row.lot_rows.length > 0 && (parseFloat(row.available_quantity) || 0) > 0,
        );
      }
      if (this.mode === "reception" && this.rows.length === 0) {
        this.rows = [this.normalizeRow({}, 0)];
      }
    },
  },
  methods: {
    round(value) {
      return parseFloat((parseFloat(value) || 0).toFixed(6));
    },
    toIsoDate(value) {
      if (!value) {
        return null;
      }
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) {
        return null;
      }
      return d.toISOString().slice(0, 10);
    },
    normalizeRow(row, idx) {
      const quantity = parseFloat(row?.quantity) || 0;
      const factor = parseFloat(this.conversionFactorKgPerUnit) || 1;
      return {
        row_key: row?.row_key || row?.group_key || row?.uuid || `${Date.now()}-${idx}`,
        stock_movement_item_uuid: row?.stock_movement_item_uuid || null,
        lot_rows: Array.isArray(row?.lot_rows) ? row.lot_rows : null,
        lot: row?.lot || "",
        expiration_date: this.toIsoDate(row?.expiration_date),
        available_quantity: parseFloat(row?.available_quantity) || 0,
        quantity,
        quantity_kg: this.round(quantity * factor),
        measure_unit: row?.measure_unit || { name: this.measureUnitSymbol },
        measure_unit_id: row?.measure_unit_id || null,
      };
    },
    addReceptionRow() {
      const nextIdx = this.rows.length;
      this.rows.push(this.normalizeRow({}, nextIdx));
      this.error = "";
    },
    onRowUpdated() {
      this.rows = this.rows.map((row, idx) => this.normalizeRow(row, idx));
      this.error = "";
    },
    onCellPrepared(e) {
      if (e.rowType !== "data" || !e.column?.allowEditing) {
        return;
      }

      e.cellElement.classList.add("editable-cell-yellow");
    },
    cancel() {
      this.$emit("resolved", {
        action: "cancel_save",
        items: [],
      });
    },
    hasUniqueReceptionRows() {
      const keys = new Set();
      for (const row of this.rows) {
        const lot = (row?.lot || "").trim().toLowerCase();
        const exp = this.toIsoDate(row?.expiration_date) || "";
        const key = `${lot}|${exp}`;
        if (keys.has(key)) {
          return false;
        }
        keys.add(key);
      }
      return true;
    },
    validateRows() {
      if (!Array.isArray(this.rows) || this.rows.length === 0) {
        this.error = this.$t("lot_items.required");
        return false;
      }

      if (this.mode === "reception") {
        const hasEmpty = this.rows.some((row) => !(row?.lot || "").trim());
        if (hasEmpty) {
          this.error = this.$t("lot_items.row_required");
          return false;
        }
        if (!this.hasUniqueReceptionRows()) {
          this.error = this.$t("lot_items.duplicate_row") || "Duplicate lot + expiration date is not allowed";
          return false;
        }
      }

      const hasInvalidQty = this.rows.some((row) => {
        const qty = parseFloat(row?.quantity) || 0;
        if (this.mode === "sales") {
          const max = parseFloat(row?.available_quantity) || 0;
          if (qty < 0) {
            return true;
          }
          return qty > max + this.epsilon;
        }
        if (qty <= 0) {
          return true;
        }
        return false;
      });
      if (hasInvalidQty) {
        this.error = this.$t("lot_items.invalid_quantity");
        return false;
      }

      const targetQty = parseFloat(this.targetQuantity) || 0;
      if (Math.abs(this.selectedTotal - targetQty) > this.epsilon) {
        this.error = this.$t("lot_items.sum_mismatch");
        return false;
      }

      const targetQtyKg = parseFloat(this.targetQuantityKg) || 0;
      if (Math.abs(this.selectedTotalKg - targetQtyKg) > this.epsilon) {
        this.error = this.$t("lot_items.sum_mismatch");
        return false;
      }

      return true;
    },
    confirm() {
      this.rows = this.rows.map((row, idx) => this.normalizeRow(row, idx));
      if (!this.validateRows()) {
        return;
      }

      let items = this.rows;
      if (this.mode === "sales") {
        items = items.filter((row) => (parseFloat(row.quantity) || 0) > 0);
      }

      let normalized;
      if (this.mode === "sales") {
        // Split each lot group quantity across its underlying stock movement
        // item rows (FIFO), keeping the payload row-level as before.
        const factor = parseFloat(this.conversionFactorKgPerUnit) || 1;
        normalized = items.flatMap((row) => {
          const split = splitQuantityAcrossLotRows(row, this.round(row.quantity));
          const totalKg = this.round(row.quantity_kg);
          let kgAssigned = 0;
          return split.map((part, i) => {
            // assign the kg remainder to the last split row to avoid rounding drift
            const kg = i === split.length - 1 ? this.round(totalKg - kgAssigned) : this.round(part.quantity * factor);
            kgAssigned = this.round(kgAssigned + kg);
            return {
              stock_movement_item_uuid: part.stock_movement_item_uuid,
              lot: (part.lot || "").trim(),
              expiration_date: this.toIsoDate(part.expiration_date),
              quantity: this.round(part.quantity),
              quantity_kg: kg,
              measure_unit_id: part.measure_unit_id || row.measure_unit_id,
            };
          });
        });
      } else {
        normalized = items.map((row) => ({
          stock_movement_item_uuid: row.stock_movement_item_uuid,
          lot: (row.lot || "").trim(),
          expiration_date: this.toIsoDate(row.expiration_date),
          quantity: this.round(row.quantity),
          quantity_kg: this.round(row.quantity_kg),
          measure_unit_id: row.measure_unit_id,
        }));
      }

      if (this.mode === "sales" && normalized.some((row) => !row.stock_movement_item_uuid)) {
        this.error = this.$t("lot_items.select_required");
        return;
      }

      this.$emit("resolved", {
        action: "confirm",
        items: normalized,
      });
    },
  },
};
</script>

<style scoped>
.wli-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.wli-title-main {
  font-size: 18px;
  font-weight: 700;
  color: #323232;
}

.wli-title-sub {
  font-size: 12px;
  color: #777;
}

.wli-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
}

.wli-toolbar {
  display: flex;
  justify-content: flex-start;
}

.wli-summary {
  display: flex;
  gap: 8px;
  font-size: 13px;
  color: #555;
}

.wli-error {
  display: block;
}

.wli-actions {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
